import type { Provider, Configuration } from '@repo/api-gw-sdk';
import {
  GetSourceStorageBackupStatusApi,
  GetBackedUpStorageDateHistogramApi,
  GetBackedUpResourcesApi,
  GetDashboardHomeDriftProtectionApi,
  GetBackupControlViolationsApi,
  DashboardApi,
  GetSecuritySummaryApi,
  GetInfectedDataByClassificationApi,
  GetInfectedDataOverTimeApi,
} from '@repo/api-gw-sdk';

import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';
const baseUrl = '/dashboard/home/';

export const dashboards = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const getSourceStorageBackupStatusApi = new GetSourceStorageBackupStatusApi(
    config
  );
  const getBackedUpStorageDateHistogramApi =
    new GetBackedUpStorageDateHistogramApi(config);
  const getBackedUpResourcesApi = new GetBackedUpResourcesApi(config);
  const getDashboardHomeDriftProtectionApi =
    new GetDashboardHomeDriftProtectionApi(config);
  const getBackupControlViolationsApi = new GetBackupControlViolationsApi(
    config
  );
  const dashboardApi = new DashboardApi(config);
  const getSecuritySummaryApi = new GetSecuritySummaryApi(config);
  const getInfectedDataByClassificationApi =
    new GetInfectedDataByClassificationApi(config);
  const getInfectedDataOverTimeApi = new GetInfectedDataOverTimeApi(config);

  return {
    useSourceStorageBackupStatus: () =>
      httpClient.execute(
        getSourceStorageBackupStatusApi.getSourceStorageBackupStatus.bind(
          getSourceStorageBackupStatusApi
        ),
        baseUrl,
        'source-storage-backup-status',
        currentProjectId
      ),
    useBackedUpStorageDateHistogram: () => {
      return httpClient.execute(
        getBackedUpStorageDateHistogramApi.getBackedUpStorageDateHistogram.bind(
          getBackedUpStorageDateHistogramApi
        ),
        baseUrl,
        'backed-up-storage-date-histogram',
        currentProjectId
      );
    },
    useBackedUpByCloudProvider: (cloudProvider: Provider) => {
      return httpClient.execute(
        getBackedUpResourcesApi.getBackedUpResources.bind(
          getBackedUpResourcesApi
        ),
        baseUrl,
        'backed-up-by-cloud-provider',
        cloudProvider,
        currentProjectId
      );
    },
    useDriftProtection: () => {
      return httpClient.execute(
        getDashboardHomeDriftProtectionApi.getDashboardHomeDriftProtection.bind(
          getDashboardHomeDriftProtectionApi
        ),
        baseUrl,
        'drift-protection',
        currentProjectId
      );
    },
    useBackupControlViolations: () => {
      return httpClient.execute(
        getBackupControlViolationsApi.getBackupControlViolations.bind(
          getBackupControlViolationsApi
        ),
        baseUrl,
        'backup-control-violations',
        currentProjectId
      );
    },
    useDailyStorageSummary: () => {
      const start = new Date();
      start.setDate(start.getDate() - 30); // 30 days ago
      const end = new Date(); // today

      const startString = dayjs.utc(start).format('YYYY-MM-DD');
      const endString = dayjs.utc(end).format('YYYY-MM-DD');

      return httpClient.execute(
        dashboardApi.getDailyStorageSummaries.bind(dashboardApi),
        baseUrl,
        'daily-storage-summary',
        currentProjectId,
        startString,
        endString
      );
    },
    security: {
      useSecuritySummary: (prod: boolean, provider?: Provider) => {
        return httpClient.execute(
          getSecuritySummaryApi.getSecuritySummary.bind(getSecuritySummaryApi),
          baseUrl,
          'security-summary',
          currentProjectId,
          prod,
          provider ? [provider] : undefined
        );
      },
      useInfectedDataByClassification: (prod: boolean, provider?: Provider) => {
        return httpClient.execute(
          getInfectedDataByClassificationApi.getInfectedDataByClassification.bind(
            getInfectedDataByClassificationApi
          ),
          baseUrl,
          'infected-by-classification',
          currentProjectId,
          prod,
          provider ? [provider] : undefined
        );
      },
      useInfectedDataOverTime: (
        numOfDays: number,
        prod: boolean,
        provider?: Provider
      ) => {
        return httpClient.execute(
          getInfectedDataOverTimeApi.getInfectedDataOverTime.bind(
            getInfectedDataOverTimeApi
          ),
          baseUrl,
          'infected-over-time',
          currentProjectId,
          numOfDays,
          prod,
          provider ? [provider] : undefined
        );
      },
    },
  };
};
